import { differenceInYears } from 'date-fns';
import type { SelfServiceRebookingTraveler } from '@eti/schema-types';
import { AgeType } from '@eti/schema-types';
import {
  passengerFields,
  PASSENGERS_FORM_SECTION,
} from '../../../widgets/search-form/constants/formFields';
import {
  CHILD_AGE_DEFAULT_VALUE,
  MAX_INFANT_AGE,
  MAX_PASSENGERS,
} from '../../../widgets/search-form/constants/passengers';

const isPassengerInputDisabled = (travelers: (SelfServiceRebookingTraveler | null)[]) =>
  travelers.every((traveler) => traveler?.ageType === AgeType.Adt);

const setGroupAges = (travelers: (SelfServiceRebookingTraveler | null)[], defaultValue: number) =>
  Array.from(Array(MAX_PASSENGERS)).map((_, index) => {
    const traveler = travelers[index];
    const travelerAge =
      traveler && traveler.birthDate
        ? differenceInYears(new Date(), new Date(traveler.birthDate))
        : null;
    return travelerAge || defaultValue;
  });

const AgeTypeKeys = {
  [AgeType.Adt]: 'adults',
  [AgeType.Cnn]: 'children',
  [AgeType.Inf]: 'infants',
} as const;

const getRebookingPassengers = (travelers: (SelfServiceRebookingTraveler | null)[]) =>
  travelers.reduce(
    (passengers, traveler) => ({
      ...passengers,
      [AgeTypeKeys[traveler?.ageType as AgeType]]: [
        ...passengers[AgeTypeKeys[traveler?.ageType as AgeType]],
        traveler,
      ],
    }),
    {
      adults: [] as (SelfServiceRebookingTraveler | null)[],
      children: [] as (SelfServiceRebookingTraveler | null)[],
      infants: [] as (SelfServiceRebookingTraveler | null)[],
    },
  );

export const getRebookingData = (
  travelers: (SelfServiceRebookingTraveler | null)[],
  criteria?: (string | null)[],
  validatingCarriers?: (string | null)[],
) => {
  if (!travelers || !Array.isArray(travelers) || travelers.length === 0) {
    return null;
  }

  const { adults, children, infants } = getRebookingPassengers(travelers);

  return {
    criteria,
    initialValues: {
      [PASSENGERS_FORM_SECTION]: {
        [passengerFields.ADULTS]: adults.length,
        [passengerFields.CHILDREN]: children.length,
        [passengerFields.CHILDREN_AGES]: setGroupAges(children, CHILD_AGE_DEFAULT_VALUE),
        [passengerFields.INFANTS]: infants.length,
        [passengerFields.INFANTS_AGES]: setGroupAges(infants, MAX_INFANT_AGE),
      },
    },
    formConstraints: {
      passengers: {
        maxNumberOfTravelers: adults.length + children.length,
        isDisabled: isPassengerInputDisabled(travelers),
      },
    },
    travelers,
    validatingCarriers,
  };
};
